import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, CircleAlert, Plus, Save, Send } from 'lucide-react';
import * as hr from '../../api';
import { hrSubUrl } from '../../catalog';
import { useHR } from '../../HRContext';
import Icon from '../../Icon';
import { DateInput, Select } from '../../components/Controls';
import { FileChip, FilePicker, openStored } from '../../components/Files';
import { REQUEST_ATTACHMENT_RULE } from '../../fileRules';
import { findModule, findType, slaFor, typeModule } from './shared';
import Loader from '../../components/Loader';

// HR Request Center > New Request (Stephanet's custom/hrrc/NewRequest.jsx): module cards + the 3-step request form
// (Request Classification → Request Details → Review & Submit). A draft opened from Drafts (?draft=id) continues in the
// same form. A type with a home (leave, attendance correction, HR case) creates that record when submitted.

const HOME_NOTE = {
  leave: 'Submitting books the leave in Leave Management; its approval (supervisor, then HR) decides this request.',
  attendance_correction: "Submitting corrects the person's attendance for the day; the Attendance Edit Log approval decides this request.",
  hr_case: 'Submitting raises an HR case in Talent & Movement; its approval decides this request.',
};

export default function NewRequest({ meta }) {
  const [params, setParams] = useSearchParams();
  const draftId = params.get('draft');
  const [start, setStart] = useState(draftId ? { draft: draftId } : null);
  useEffect(() => { if (draftId) setStart({ draft: draftId }); }, [draftId]);
  const close = () => { setStart(null); if (draftId) setParams({}); };
  if (start) return <div className="mdr-card hrc-wizard-card"><RequestWizard meta={meta} draftId={start.draft} presetModule={start.module} onDone={close} onCancel={close} /></div>;
  return (
    <>
      <div className="hrc-hero">
        <h3>Create a New Request</h3>
        <p>Use the dynamic request form to create requests tailored to your module. Each module has specific fields and approval workflows.</p>
        {meta.can.create
          ? <button className="mds-run" onClick={() => setStart({})}><Plus size={15} /> Start New Request</button>
          : <p className="hrc-warn"><CircleAlert size={14} /> Your role cannot create requests in the Request Center.</p>}
      </div>
      <div className="hrc-modgrid">
        {meta.modules.map((m, i) => (
          <button key={m.code} type="button" className={`hrc-modcard c-${m.color}`} style={{ animationDelay: `${i * 25}ms` }}
                  disabled={!meta.can.create} onClick={() => setStart({ module: m.code })}>
            <span className="hrc-modicon"><Icon name={m.icon} size={18} /></span>
            <span className="hrc-modtext"><b>{m.label}</b><small>{m.types.length} request types available</small></span>
            <span className="hrc-modbadge">{m.types.length}</span>
          </button>
        ))}
      </div>
    </>
  );
}

const EMPTY = { module: '', type_id: '', title: '', priority: 'Medium', required_date: '', site_id: '' };

export function RequestWizard({ meta, draftId, presetModule, onDone, onCancel }) {
  const { notify } = useHR();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ ...EMPTY, module: presetModule || '' });
  const [details, setDetails] = useState({});
  const [errors, setErrors] = useState({});
  const [rec, setRec] = useState(null);
  const [pending, setPending] = useState({});   // field key → [File]
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!draftId);

  useEffect(() => {
    if (!draftId) return;
    hr.hrrcGet(draftId).then(r => {
      if (!r.editable) { notify(`${r.request_no} can no longer be edited`, 'bad'); onCancel(); return; }
      setRec(r);
      setF({ module: typeModule(meta, r.type_id)?.code || '', type_id: r.type_id || '', title: r.title || '', priority: r.priority || 'Medium',
        required_date: r.required_date || '', site_id: r.site_id || '' });
      setDetails(r.details || {});
    }).catch(e => { notify(e.message, 'bad'); onCancel(); }).finally(() => setLoading(false));
  }, [draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  const mod = findModule(meta, f.module);
  const type = findType(meta, f.type_id);
  const sla = slaFor(type, f.priority, meta.priorities);
  const fields = useMemo(() => type?.fields || [], [type]);
  const files = useMemo(() => {
    const byField = {};
    (rec?.files || []).forEach(x => {
      const key = fields.find(fl => String(fl.id) === String(x.field_id))?.key;
      if (key) (byField[key] = byField[key] || []).push(x);
    });
    return byField;
  }, [rec, fields]);
  const set = (k, v) => { setF(s => ({ ...s, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const setD = (k, v) => { setDetails(s => ({ ...s, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const pickEmployee = id => {
    setD('employee', id);
    const e = meta.employees.find(x => String(x.id) === String(id));
    if (e && fields.some(x => x.key === 'department') && !details.department && e.department_id) setD('department', String(e.department_id));
  };

  const check1 = () => {
    const e = {};
    if (!f.module) e.module = 'Request module is required';
    if (!f.type_id) e.type_id = 'Request type is required';
    if (!f.title.trim()) e.title = 'Request title is required';
    setErrors(e);
    return !Object.keys(e).length;
  };
  const check2 = () => {
    const e = {};
    fields.filter(x => x.required).forEach(x => {
      const has = x.kind === 'file' ? (files[x.key]?.length || pending[x.key]?.length) : details[x.key] !== undefined && details[x.key] !== '' && details[x.key] !== null;
      if (!has) e[x.key] = `${x.label} is required`;
    });
    if (type?.attachment_required && !Object.values(files).some(a => a.length) && !Object.values(pending).some(a => a.length)) e.attachment = 'This request type needs an attachment';
    if (details.startDate && details.endDate && details.endDate < details.startDate) e.endDate = 'End date cannot be before the start date';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const payload = submit => ({ type_id: f.type_id || null, title: f.title, priority: f.priority, required_date: f.required_date || null,
    site_id: f.site_id || null, details, submit });
  const save = async submit => {
    if (!check1()) { setStep(1); return; }
    if (submit && !check2()) { setStep(2); notify('Complete the required fields before submitting', 'bad'); return; }
    setBusy(true);
    try {
      const hasPending = Object.values(pending).some(a => a.length);
      let id = rec?.id;
      let res = id ? await hr.hrrcUpdate(id, payload(submit && !hasPending)) : await hr.hrrcCreate(payload(submit && !hasPending));
      id = res.id || id;
      if (hasPending) {
        for (const [k, list] of Object.entries(pending)) for (const file of list) await hr.hrrcAddFile(id, k, file);
        setPending({});
        if (submit) res = await hr.hrrcUpdate(id, payload(true));
      }
      notify(res.message);
      if (submit) { onDone(); navigate(hrSubUrl('hrrc-all', { open: id })); }
      else { const r = await hr.hrrcGet(id); setRec(r); setDetails(r.details || {}); }
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const addFiles = (k, list) => {
    setPending(p => ({ ...p, [k]: [...(p[k] || []), ...list] }));
    setErrors(e => ({ ...e, [k]: undefined, attachment: undefined }));
  };
  const removeSaved = async x => {
    try { await hr.hrrcRemoveFile(rec.id, x.id); setRec(await hr.hrrcGet(rec.id)); } catch (e) { notify(e.message, 'bad'); }
  };

  if (loading) return <Loader title="Loading draft…" />;
  const Err = ({ k }) => (errors[k] ? <p className="hrc-err">{errors[k]}</p> : null);
  const Lbl = ({ x }) => <span>{x.label} {x.required && <b className="hrc-req">*</b>}</span>;
  const shown = x => {
    const v = details[x.key];
    if (x.kind === 'file') return `${((files[x.key]?.length || 0) + (pending[x.key]?.length || 0)) || 'No'} file(s)`;
    if (x.kind === 'employee') return meta.employees.find(e => String(e.id) === String(v))?.name || '—';
    if (x.kind === 'department') return meta.departments.find(e => String(e.id) === String(v))?.name || '—';
    return v || '—';
  };

  const field = x => {
    const k = x.key;
    const v = details[k] ?? '';
    const cls = errors[k] ? 'bad' : '';
    if (x.kind === 'file') {
      return (
        <div key={k} className="hrc-f span2">
          <Lbl x={x} />
          <FilePicker rule={REQUEST_ATTACHMENT_RULE} multiple label="Click to upload or drag files" onPick={list => addFiles(k, list)} onError={m => notify(m, 'bad')} />
          <div className="hrc-files">
            {(files[k] || []).map(fl => <FileChip key={fl.id} name={fl.name} size={fl.size} onOpen={() => openStored(() => hr.hrrcFileBlob(rec.id, fl.id))} onRemove={() => removeSaved(fl)} />)}
            {(pending[k] || []).map((fl, i) => <FileChip key={i} name={fl.name} size={fl.size} pending onRemove={() => setPending(p => ({ ...p, [k]: p[k].filter((_, j) => j !== i) }))} />)}
          </div>
          <Err k={k} />
        </div>
      );
    }
    let input;
    if (x.kind === 'date') input = <DateInput className={cls} value={v} onChange={e => setD(k, e.target.value)} />;
    else if (x.kind === 'number') input = <input type="number" min="0" className={cls} value={v} placeholder="0" onChange={e => setD(k, e.target.value)} />;
    else if (x.kind === 'choice') input = <Select className={cls} value={v} onChange={e => setD(k, e.target.value)} title={x.label}><option value="">Select {x.label.toLowerCase()}...</option>{x.options.map(o => <option key={o} value={o}>{o}</option>)}</Select>;
    else if (x.kind === 'employee') input = <Select className={cls} value={v} onChange={e => (k === 'employee' ? pickEmployee(e.target.value) : setD(k, e.target.value))} title={x.label}><option value="">Select employee...</option>{meta.employees.map(e => <option key={e.id} value={String(e.id)}>{e.name} ({e.employee_no})</option>)}</Select>;
    else if (x.kind === 'department') input = <Select className={cls} value={v} onChange={e => setD(k, e.target.value)} title={x.label}><option value="">Select department...</option>{meta.departments.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}</Select>;
    else if (x.kind === 'long_text') input = <textarea rows={3} className={cls} value={v} placeholder={`Enter ${x.label.toLowerCase()}...`} onChange={e => setD(k, e.target.value)} />;
    else input = <input className={cls} value={v} placeholder={`Enter ${x.label.toLowerCase()}...`} onChange={e => setD(k, e.target.value)} />;
    return <label key={k} className={`hrc-f ${x.kind === 'long_text' ? 'span2' : ''}`}><Lbl x={x} />{input}<Err k={k} /></label>;
  };
  const site = meta.sites.find(s => String(s.id) === String(f.site_id))?.name;

  return (
    <div className="hrc-wizard">
      <div className="hrc-steps">
        {[1, 2, 3].map((n, i) => (
          <div key={n} className="hrc-step-wrap"><span className={`hrc-step ${step >= n ? 'on' : ''}`}>{n}</span>{i < 2 && <span className={`hrc-step-bar ${step > n ? 'on' : ''}`} />}</div>
        ))}
      </div>
      <div className="hrc-wcard">
        <h3>{['Request Classification', 'Request Details', 'Review & Submit'][step - 1]}</h3>
        <p className="hrc-sub">{['Select your request module, type, and basic information', 'Provide module-specific details', 'Review your request before submission'][step - 1]}</p>

        {step === 1 && (
          <div className="hrc-form">
            <label className="hrc-f span2"><span>Request Module *</span>
              <Select className={errors.module ? 'bad' : ''} value={f.module} title="Request module" onChange={e => { setF(s => ({ ...s, module: e.target.value, type_id: '' })); setDetails({}); setErrors({}); }}>
                <option value="">Select request module...</option>{meta.modules.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
              </Select><Err k="module" />
            </label>
            {mod && (
              <label className="hrc-f span2"><span>Request Type *</span>
                <Select className={errors.type_id ? 'bad' : ''} value={String(f.type_id)} title="Request type" onChange={e => set('type_id', e.target.value)}>
                  <option value="">Select request type...</option>{mod.types.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                </Select><Err k="type_id" />
              </label>
            )}
            <label className="hrc-f span2"><span>Request Title *</span>
              <input className={errors.title ? 'bad' : ''} value={f.title} onChange={e => set('title', e.target.value)} placeholder="Enter a clear title for this request..." maxLength={200} /><Err k="title" />
            </label>
            <label className="hrc-f"><span>Priority *</span>
              <Select value={f.priority} onChange={e => set('priority', e.target.value)} title="Priority">{meta.priorities.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}</Select>
            </label>
            <label className="hrc-f"><span>Required Date</span><DateInput value={f.required_date} onChange={e => set('required_date', e.target.value)} /></label>
          </div>
        )}

        {step === 2 && (
          <>
            <div className="hrc-summary"><p><b>Module:</b> {mod?.label}</p><p><b>Type:</b> {type?.name}</p>
              {type?.attachment_required && <p><b>Attachment:</b> required</p>}
              {type?.cost_implication && <p><b>Cost implication:</b> yes{type.cost_threshold ? ` (threshold ₦${type.cost_threshold.toLocaleString()})` : ''}</p>}</div>
            {type && HOME_NOTE[type.home] && <p className="hrc-sub">{HOME_NOTE[type.home]}</p>}
            {errors.attachment && <p className="hrc-err">{errors.attachment}</p>}
            <div className="hrc-form">
              {fields.map(field)}
              <label className="hrc-f"><span>Project / Site</span>
                <Select value={String(f.site_id || '')} onChange={e => set('site_id', e.target.value)} title="Project / site">
                  <option value="">Select site...</option>{meta.sites.map(x => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                </Select>
              </label>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="hrc-review">
            <div className="hrc-review-box">
              <div className="hrc-kv"><div><small>Request ID</small><b className="mono">{!rec || rec.status === 'draft' ? `${rec?.request_no || 'Draft'} → request number on submit` : rec.request_no}</b></div>
                <div><small>Module</small><b>{mod?.label}</b></div><div><small>Type</small><b>{type?.name}</b></div>
                <div><small>Priority</small><b><span className={`mdr-chip t-${f.priority === 'Critical' ? 'darkred' : 'gray'}`}>{f.priority}</span></b></div></div>
              <div className="hrc-kv one"><div><small>Title</small><b>{f.title}</b></div></div>
              <div className="hrc-kv"><div><small>Project / Site</small><b>{site || '—'}</b></div>
                <div><small>Required Date</small><b>{f.required_date || '—'}</b></div><div><small>SLA</small><b>{sla.text}</b></div></div>
              <div className="hrc-kv">{fields.map(x => <div key={x.key}><small>{x.label}</small><b>{shown(x)}</b></div>)}</div>
            </div>
            {type?.route?.length > 0 && (
              <div className="hrc-route"><p>Approval Route:</p>
                <div>{type.route.map((r, i) => <span key={r + i}><span className="hrc-route-chip">{r}</span>{i < type.route.length - 1 && <ChevronRight size={13} />}</span>)}</div>
              </div>
            )}
            <p className="hrc-sub">By clicking Submit, this request will be sent for approval according to the approval route above.</p>
          </div>
        )}
      </div>
      <div className="hrc-wfoot">
        <div>{step > 1 && <button className="mdr-btn" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>}</div>
        <div>
          <button className="mdr-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          {step < 3 ? (<>
            {step === 2 && <button className="mdr-btn" onClick={() => save(false)} disabled={busy}><Save size={14} /> Save as Draft</button>}
            <button className="mds-run" onClick={() => { if (step === 1 ? check1() : (check2(), true)) setStep(step + 1); }} disabled={busy}>Next</button>
          </>) : (<>
            <button className="mdr-btn" onClick={() => save(false)} disabled={busy}><Save size={14} /> Save as Draft</button>
            <button className="mds-run" onClick={() => save(true)} disabled={busy}><Send size={14} /> {busy ? 'Submitting…' : 'Submit Request'}</button>
          </>)}
        </div>
      </div>
    </div>
  );
}
